import { Component, OnInit, AfterViewChecked, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ElementRef, ViewChild } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { OpportunityService, CompanyService, PartnerService, CurrencyService, LocationTransportService, getSafeImageUrl } from '@app/core';
import { finalize, forkJoin, Observable } from 'rxjs';
import { GetParams, BudgetLine, SaleOrderLineTaxRel } from '@app/shared';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { buildBudgetCustomerAddressLines, buildBudgetCustomerNameLine } from './budget-preview-address.utils';
import { resolveBudgetCustomer } from './budget-preview-customer.utils';
import { ConditionsPageKind, paginateBudgetConditions, paginateBudgetConditionsAsync } from './budget-preview-conditions.utils';
import {
  BudgetLineFragment,
  BudgetPdfPage,
  fragmentMeasuredBudgetLine,
  isBudgetContentWithinSafeArea,
  paginateMeasuredBudgetLines,
} from './budget-preview-pagination.utils';
import { buildBudgetTaxSummary, resolveStoredLineTax } from './budget-preview-tax-summary.utils';
import {
  convertFixedTax,
  convertLineAmounts,
  IssueCurrencyParams,
  IssueCurrencyRates,
  parseIssueCurrencyParams,
  sumDecimal,
} from './budget-preview-issue-currency.utils';
import {
  BudgetLineAmountsInput,
  resolveGrossLineAmount,
  resolveLineDiscountPercent,
  resolveNetLineAmount,
  resolveNetLineAmountBeforeDiscount,
  resolveNetUnitPrice,
  shouldDiscloseBudgetDiscount,
} from './budget-preview-line-amounts.utils';
import { resolveSaleOrderPdfTitle } from '../../../opportunities/quotation-name-display.util';

export function formatBudgetDateOnly(value: string | Date | null | undefined): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const isoTimestampMatch = /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?$/.exec(value);
    const calendarMatch = dateOnlyMatch ?? isoTimestampMatch;

    if (!calendarMatch || (isoTimestampMatch && Number.isNaN(Date.parse(value)))) {
      return '';
    }

    const [, yearText, monthText, dayText] = calendarMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const candidate = new Date(Date.UTC(year, month - 1, day));

    if (
      candidate.getUTCFullYear() !== year
      || candidate.getUTCMonth() !== month - 1
      || candidate.getUTCDate() !== day
    ) {
      return '';
    }

    return `${dayText}/${monthText}/${yearText}`;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

export async function waitForBudgetConditionsLayout(
  documentRef: Document,
  requestFrame: (callback: FrameRequestCallback) => number = requestAnimationFrame
): Promise<void> {
  try {
    await documentRef.fonts?.ready;
  } catch {
    // La medición conserva un fallback incluso si el navegador no expone/carga FontFaceSet.
  }
  await new Promise<void>(resolve => requestFrame(() => resolve()));
  await new Promise<void>(resolve => requestFrame(() => resolve()));
}

export interface BudgetMeasurementWaitOptions {
  timeoutMs?: number;
  /** Absolute generation deadline; when present it replaces the per-stage timeout. */
  deadlineAt?: number;
  requestFrame?: (callback: FrameRequestCallback) => number;
  isDataStable?: () => boolean;
  includeDecorativeImages?: boolean;
}

export class BudgetMeasurementTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`El layout del presupuesto no se estabilizó en ${timeoutMs} ms.`);
    this.name = 'BudgetMeasurementTimeoutError';
  }
}

export async function waitForBudgetMeasurementLayout(
  documentRef: Document,
  root: HTMLElement,
  options: BudgetMeasurementWaitOptions = {}
): Promise<void> {
  const timeoutMs = options.deadlineAt === undefined
    ? options.timeoutMs ?? 5000
    : Math.max(0, options.deadlineAt - Date.now());
  const requestFrame = options.requestFrame ?? requestAnimationFrame;
  const isDataStable = options.isDataStable ?? (() => true);
  const settledImages = new WeakSet<HTMLImageElement>();
  const observedImages = new WeakSet<HTMLImageElement>();
  const cleanups: Array<() => void> = [];
  let cancelled = false;

  const observeImage = (image: HTMLImageElement): void => {
    if (image.complete || observedImages.has(image)) return;
    observedImages.add(image);
    const settle = () => settledImages.add(image);
    image.addEventListener('load', settle, { once: true });
    image.addEventListener('error', settle, { once: true });
    cleanups.push(() => {
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
    });
  };

  const nextFrame = () => new Promise<void>(resolve => requestFrame(() => resolve()));
  const stabilize = async (): Promise<void> => {
    try {
      await documentRef.fonts?.ready;
    } catch {
      // FontFaceSet may reject in restricted browser contexts; frame/height checks still guard measurement.
    }

    let previousHeight: number | null = null;
    let stableFrames = 0;
    while (!cancelled) {
      await nextFrame();
      if (cancelled) return;

      const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
      images.forEach(observeImage);
      const imagesStable = images.every(image => image.complete
        || settledImages.has(image)
        || image.dataset['pdfResourceSettled'] === 'true'
        || (!options.includeDecorativeImages && image.dataset['pdfDecorative'] === 'true'));
      const height = Math.max(root.scrollHeight, root.getBoundingClientRect().height);
      const prerequisitesStable = imagesStable && isDataStable();

      stableFrames = prerequisitesStable && previousHeight === height ? stableFrames + 1 : 0;
      previousHeight = height;
      if (stableFrames >= 2) return;
    }
  };

  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new BudgetMeasurementTimeoutError(timeoutMs)), timeoutMs);
  });

  try {
    await Promise.race([stabilize(), timeout]);
  } finally {
    cancelled = true;
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    cleanups.forEach(cleanup => cleanup());
  }
}

export interface BudgetPdfRenderManifest {
  expectedLineCount: number;
  renderedLineCount: number;
  pageCount: number;
  summaryRendered: boolean;
  conditionsRendered: boolean;
  stable: true;
}

export interface BudgetPdfRenderSnapshot {
  expectedLineIds: string[];
  renderedFragments: Array<{ lineId: string; fragmentKey: string }>;
  expectedPageCount: number;
  renderedPageCount: number;
  summaryRequired: boolean;
  summaryCount: number;
  conditionsExpected: boolean;
  conditionsCount: number;
}

export function canonicalizeBudgetLines<T extends { id?: unknown }>(lines: T[]): T[] {
  const ids = new Set<number>();
  const canonical = [...lines];
  for (const line of canonical) {
    if (!Number.isInteger(line.id) || Number(line.id) <= 0 || Number(line.id) > 2147483647) {
      throw new Error('El presupuesto contiene una línea sin identificador numérico positivo.');
    }
    if (ids.has(Number(line.id))) {
      throw new Error('El presupuesto contiene identificadores de línea duplicados.');
    }
    ids.add(Number(line.id));
  }
  return canonical.sort((left, right) => Number(left.id) - Number(right.id));
}

export function buildBudgetPdfRenderManifest(snapshot: BudgetPdfRenderSnapshot): BudgetPdfRenderManifest {
  const fragmentKeys = snapshot.renderedFragments.map(fragment => fragment.fragmentKey);
  if (new Set(fragmentKeys).size !== fragmentKeys.length) {
    throw new Error('El DOM final contiene claves de fragmento duplicadas.');
  }
  const invalidFragmentKey = snapshot.renderedFragments.some(fragment => {
    const prefix = `${fragment.lineId}:`;
    return fragment.fragmentKey !== fragment.lineId
      && !(fragment.fragmentKey.startsWith(prefix) && /^\d+$/.test(fragment.fragmentKey.slice(prefix.length)));
  });
  if (invalidFragmentKey) throw new Error('La clave de fragmento no pertenece a su línea lógica.');
  let previousLineId = 0;
  let currentLineId: string | null = null;
  let expectedFragmentIndex = 0;
  let baseFragmentSeen = false;
  for (const fragment of snapshot.renderedFragments) {
    if (currentLineId !== fragment.lineId) {
      if (!/^[1-9]\d*$/.test(fragment.lineId)
        || Number(fragment.lineId) > 2147483647
        || Number(fragment.lineId) <= previousLineId) {
        throw new Error('Las líneas del presupuesto no están en orden canónico.');
      }
      currentLineId = fragment.lineId;
      previousLineId = Number(fragment.lineId);
      expectedFragmentIndex = 0;
      baseFragmentSeen = false;
    }

    if (fragment.fragmentKey === fragment.lineId) {
      if (baseFragmentSeen || expectedFragmentIndex !== 0) {
        throw new Error(`Los fragmentos de la línea ${fragment.lineId} no están en orden canónico.`);
      }
      baseFragmentSeen = true;
      continue;
    }

    const indexText = fragment.fragmentKey.slice(fragment.lineId.length + 1);
    if (baseFragmentSeen
      || !/^\d+$/.test(indexText)
      || Number(indexText) > 2147483647
      || Number(indexText) !== expectedFragmentIndex) {
      throw new Error(`Los fragmentos de la línea ${fragment.lineId} no están en orden canónico.`);
    }
    expectedFragmentIndex += 1;
  }
  const expected = new Set(snapshot.expectedLineIds);
  if (snapshot.renderedFragments.some(fragment => !expected.has(fragment.lineId))) {
    throw new Error('El DOM final contiene una línea que no pertenece al presupuesto.');
  }
  const renderedLineIds = snapshot.renderedFragments
    .map(fragment => fragment.lineId)
    .filter((id, index, ids) => ids.indexOf(id) === index);
  const linesMatch = snapshot.expectedLineIds.length === renderedLineIds.length
    && snapshot.expectedLineIds.every((id, index) => id === renderedLineIds[index]);
  if (!linesMatch) throw new Error('El DOM final no contiene exactamente las líneas esperadas.');
  if (snapshot.expectedPageCount <= 0 || snapshot.renderedPageCount !== snapshot.expectedPageCount) {
    throw new Error('El conteo de páginas renderizadas no coincide con el modelo.');
  }
  if (snapshot.summaryCount !== (snapshot.summaryRequired ? 1 : 0)) {
    throw new Error('El resumen final no coincide con el modo del presupuesto.');
  }
  if ((snapshot.conditionsCount > 0) !== snapshot.conditionsExpected) {
    throw new Error('Las condiciones finales no coinciden con el contenido procesado.');
  }
  return {
    expectedLineCount: snapshot.expectedLineIds.length,
    renderedLineCount: renderedLineIds.length,
    pageCount: snapshot.renderedPageCount,
    summaryRendered: snapshot.summaryCount === 1,
    conditionsRendered: snapshot.conditionsCount > 0,
    stable: true,
  };
}

export function validateBudgetPageSafeAreas(root: HTMLElement): void {
  const pages = Array.from(root.querySelectorAll<HTMLElement>(':scope > .page'));
  pages.forEach((page, index) => {
    const header = page.querySelector<HTMLElement>('.document-header');
    const footer = page.querySelector<HTMLElement>('.footer-section');
    if (!header || !footer) {
      throw new Error(`La página ${index + 1} no contiene el encabezado y footer requeridos.`);
    }

    const content = Array.from(
      page.querySelectorAll<HTMLElement>('.services-section, .summary-section, .conditions-section')
    ).map(element => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom };
    });
    if (!isBudgetContentWithinSafeArea({
      headerBottom: header.getBoundingClientRect().bottom,
      footerTop: footer.getBoundingClientRect().top,
      content,
    })) {
      throw new Error(`El contenido de la página ${index + 1} invade el área reservada del encabezado o footer.`);
    }
  });
}

export async function waitForBudgetFinalLayout(
  documentRef: Document,
  root: HTMLElement,
  options: BudgetMeasurementWaitOptions = {}
): Promise<void> {
  const decorativeImages = Array.from(root.querySelectorAll<HTMLImageElement>('img[data-pdf-decorative="true"]'));
  const cleanups = decorativeImages.map(image => {
    const settle = (event: Event) => {
      image.dataset['pdfResourceSettled'] = 'true';
      if (event.type === 'error') image.dataset['pdfResourceFailed'] = 'true';
    };
    image.addEventListener('load', settle);
    image.addEventListener('error', settle);
    return () => {
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
    };
  });
  try {
    await waitForBudgetMeasurementLayout(documentRef, root, { ...options, includeDecorativeImages: true });
  } catch (error) {
    const pending = Array.from(root.querySelectorAll<HTMLImageElement>('img'))
      .filter(image => !image.complete && image.dataset['pdfResourceSettled'] !== 'true');
    if (!(error instanceof BudgetMeasurementTimeoutError)
      || pending.length === 0
      || pending.some(image => image.dataset['pdfDecorative'] !== 'true')) throw error;
    pending.forEach(image => {
      image.dataset['pdfResourceSettled'] = 'true';
      image.dataset['pdfResourceFailed'] = 'true';
    });
    await waitForBudgetMeasurementLayout(documentRef, root, { ...options, includeDecorativeImages: true });
  } finally {
    cleanups.forEach(cleanup => cleanup());
  }
}

export async function retryBudgetConditionsMeasurement(
  measure: () => Promise<boolean>,
  requestFrame: (callback: FrameRequestCallback) => number = requestAnimationFrame
): Promise<boolean> {
  try {
    return await measure();
  } catch (error) {
    if (error instanceof BudgetMeasurementTimeoutError) throw error;
    await new Promise<void>(resolve => requestFrame(() => resolve()));
    return measure();
  }
}

type BudgetPdfItem = {
  icon?: string;
  label?: string;
  value: string;
};

type BudgetPdfVm = {
  issuer: {
    name: string;
    country?: string;
    logo?: string;
    items: BudgetPdfItem[];
    footerItems: BudgetPdfItem[];
  };
  customer: {
    /** Empresa y contacto en una linea, ya unidos por coma (CEC-617). */
    nameLine?: string;
    addressLines: string[];
    commercial?: string;
    nif?: string;
  };
  title: string;
  currencyLabel?: string;
  budgetDate?: string;
  expirationDate?: string;
};

const BUDGET_PDF_RENDER_DEADLINE_MS = 60_000;

@Component({
  selector: 'app-budget-preview',
  standalone: false,
  templateUrl: './budget-preview.component.html',
  styleUrl: './budget-preview.component.less'
})
export class BudgetPreviewComponent implements OnInit, AfterViewChecked, AfterViewInit {
  saleOrderId: string | null = null;
  leadId: number | null = null;
  budgetLines: BudgetLine[] = [];

  isLoading = true;
  unitary = false;
  errorMessage: string | null = null;
  /** CEC-570: conversion requested by finance through the query parameters (none by default). */
  private issueCurrency: IssueCurrencyParams = { kind: 'none' };
  isPdfReady = false;
  renderManifest: BudgetPdfRenderManifest | null = null;
  logoLoaded = false;
  countryLoaded = false;

  // InformaciÃ³n del sale order
  saleOrder: any = null;
  partner: any = null;
  commercial: any = null;
  currency: any = null;
  company: any = null;
  opportunity: any = null;
  budgetPdfVm: BudgetPdfVm | null = null;

  // InformaciÃ³n extraÃ­da de las lÃ­neas
  currencyId: number | null = null;
  saleOrderName: string = '';
  saleOrderReference: string = '';
  budgetDate: string = '';
  expirationDate: string = '';

  // Propiedades calculadas para la vista
  currencyName: string = '';
  currencyCode: string = '';
  currentDateFormatted: string = '';
  countryName: string = '';

  // Totales
  totalAmount: number = 0;
  totalDiscount: number = 0;
  totalIva: number = 0;
  totalWithIva: number = 0;
  baseAmount: number = 0; // Importe base sin descuentos
  /** Desglosa «Importe base» y «Descuento» solo cuando hay descuento (CEC-617). */
  showDiscountBreakdown: boolean = false;

  // Condiciones generales (placeholder - deberÃ­a venir del backend)
  generalConditions: string[] = [];

  // Paginado
  pages: BudgetPdfPage<BudgetLine>[] = [];
  pageNumbers: number[] = [0]; // Inicializar con al menos una pÃ¡gina
  currentPage: number = 0;
  totalPages: number = 1;
  conditionsPages: string[] = []; // Condiciones divididas por pÃ¡gina
  conditionsStartPage: number = -1; // PÃ¡gina donde empiezan las condiciones


  // Access token para peticiones manuales
  accessToken: string | null = null;

  protected readonly environment = environment;
  protected readonly getSafeImageUrl = getSafeImageUrl;
  @ViewChild('conditionsMeasurementViewport') private conditionsMeasurementViewport?: ElementRef<HTMLElement>;
  @ViewChild('conditionsMeasurementContent') private conditionsMeasurementContent?: ElementRef<HTMLElement>;
  conditionsMeasurementHtml = '';
  private viewChecked = false;
  private conditionsPaginationComplete = false;
  private pendingVisibleFallbacks = 0;
  private renderGeneration = 0;
  private renderDeadlineAt = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private opportunityService: OpportunityService,
    private companyService: CompanyService,
    private partnerService: PartnerService,
    private currencyService: CurrencyService,
    private locationTransportService: LocationTransportService,
    private cdr: ChangeDetectorRef,
    private oidcSecurityService: OidcSecurityService
  ) { }

  ngOnInit(): void {
    // Inicializar company ID desde localStorage si no estÃ¡ disponible
    // Esto es necesario cuando se abre en una nueva pestaÃ±a
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId && !this.companyService.selectedCompanyId$.getValue()) {
      this.companyService.selectedCompanyId = Number(storedCompanyId);
    }

    // Obtener el access token de la cookie 'jwt' (similar a pdf.component.ts)
    // Esto es necesario cuando se abre en una nueva pestaÃ±a
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()!.split(';').shift() || null;
      return null;
    };
    this.accessToken = getCookie('jwt');

    // El interceptor cookieTokenInterceptor manejarÃ¡ automÃ¡ticamente el token de la cookie
    // para todas las peticiones HTTP, asÃ­ que no necesitamos inicializar el servicio OIDC
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.route.params.subscribe(params => {
      this.saleOrderId = params['saleOrderId'];
      this.leadId = params['leadId'] ? Number(params['leadId']) : null;

      // Leer parÃ¡metro unitary de queryParams
      this.unitary = this.route.snapshot.queryParams['unitary'] === 'true';

      if (!this.saleOrderId) {
        this.errorMessage = 'ParÃ¡metro de presupuesto (saleOrderId) no vÃ¡lido.';
        this.isLoading = false;
        return;
      }

      this.loadBudgetData();
    });
  }

  ngAfterViewChecked(): void {
    // Readiness is published only after final DOM validation.
  }

  ngAfterViewInit(): void {
    // InicializaciÃ³n despuÃ©s de la vista
  }

  onLogoLoaded(event?: Event): void {
    const image = event?.target as HTMLImageElement | null;
    if (image) image.dataset['pdfResourceSettled'] = 'true';
    this.logoLoaded = true;
  }

  onLogoError(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.dataset['pdfResourceSettled'] = 'true';
    image.dataset['pdfResourceFailed'] = 'true';
    this.logoLoaded = true;
  }

  private checkIfReady(): void {
    // Legacy callers may update visible data, but only final manifest publication can mark readiness.
  }

  resetRenderReadiness(): number {
    this.renderGeneration += 1;
    this.renderDeadlineAt = Date.now() + BUDGET_PDF_RENDER_DEADLINE_MS;
    this.renderManifest = null;
    this.isPdfReady = false;
    this.viewChecked = false;
    return this.renderGeneration;
  }

  private loadBudgetData(): void {
    const loadGeneration = this.resetRenderReadiness();
    this.isLoading = true;
    this.errorMessage = null;

    if (!this.saleOrderId) {
      this.errorMessage = 'ID de presupuesto no vÃ¡lido.';
      this.isLoading = false;
      return;
    }

    const saleOrderIdNum = Number(this.saleOrderId);

    // Preparar las peticiones
    const saleOrderParams: GetParams & { leadId?: number } = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc' as 'asc' | 'desc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: saleOrderIdNum.toString(),
      leadId: this.leadId || undefined
    };
    const requests: any = {
      saleOrder: this.opportunityService.getSaleOrders(saleOrderParams, this.accessToken || undefined),
      lines: this.opportunityService.getAllSaleOrderLines(saleOrderIdNum, this.accessToken || undefined)
    };

    // Si hay leadId, obtener el contrato completo para PDF desde Core.
    if (this.leadId) {
      requests.opportunity = this.opportunityService.getOpportunityBudgetPdfData(this.leadId, this.accessToken || undefined);
    }

    // Cargar el sale order completo, las lÃ­neas y la oportunidad en paralelo
    forkJoin(requests).subscribe({
      next: (response: any) => {
        if (!this.isCurrentRender(loadGeneration)) return;
        const { saleOrder, lines, opportunity } = response;

        // Procesar Opportunity para obtener informaciÃ³n de la compaÃ±Ã­a, paÃ­s, cliente/contacto y comercial.
        const opportunityData = this.extractOpportunityData(opportunity);
        if (opportunityData) {
          this.opportunity = opportunityData;

          // Obtener datos de la compaÃ±Ã­a desde opportunity.company
          if (this.opportunity.company) {
            this.company = this.opportunity.company;
            // Cargar el paÃ­s desde company.countryId
            if (this.company.countryId) {
              this.loadCountryFromCompany();
            } else {
              // Si no hay countryId, marcar como cargado
              this.countryLoaded = true;
              this.checkIfReady();
            }
          } else {
            // Si no hay company, marcar como cargado
            this.countryLoaded = true;
            this.checkIfReady();
          }

          // Obtener el comercial desde opportunity.user
          if (this.opportunity.user) {
            this.loadCommercialFromUser();
          }
        } else {
          // Si no hay opportunity, marcar como cargado
          this.countryLoaded = true;
          this.checkIfReady();
        }

        // Procesar Sale Order
        this.saleOrder = saleOrder?.data?.rows?.[0] || null;
        if (this.saleOrder) {
          this.saleOrderReference = this.saleOrder.budgetCode || this.saleOrder.reference || (this.saleOrderId ? `S${this.saleOrderId.padStart(5, '0')}` : '');
          this.saleOrderName = this.saleOrder.name || 'Presupuesto';
          this.currencyId = this.saleOrder.currencyId || null;
          if (!this.resolveIssueCurrency()) return;

          // Usar datos que ya vienen en el saleOrder si estÃ¡n disponibles
          if (this.saleOrder.partner) {
            this.partner = this.saleOrder.partner;
          }

          if (this.saleOrder.currency) {
            this.currency = this.saleOrder.currency;
          }

          // Calcular propiedades de moneda
          this.updateCurrencyProperties();

          // Si no viene partner completo, intentar obtenerlo
          if (!this.partner && this.saleOrder.partnerId) {
            const partnerParams: GetParams = {
              startRow: 0,
              endRow: 1,
              sortDirection: 'asc',
              sortProperty: 'id',
              filterProperty: 'id',
              filterValue: this.saleOrder.partnerId.toString()
            };
            this.trackVisibleFallback(
              this.partnerService.getPartner(partnerParams, this.accessToken || undefined)
            ).subscribe({
              next: (response) => {
                if (response?.data?.rows?.[0]) {
                  this.partner = response.data.rows[0];
                  this.refreshBudgetPdfVm();
                }
              },
              error: (err) => {
                console.error('[BudgetPreview] Error en getPartner:', {
                  partnerId: this.saleOrder.partnerId,
                  error: err,
                  status: err?.status,
                  message: err?.message
                });
              }
            });
          }

          // Si no viene currency completo, intentar obtenerlo
          if (!this.currency && this.currencyId) {
            const currencyParams: GetParams = {
              startRow: 0,
              endRow: 1,
              sortDirection: 'asc',
              sortProperty: 'id',
              filterProperty: 'id',
              filterValue: this.currencyId.toString()
            };
            this.trackVisibleFallback(
              this.currencyService.getCurrency(currencyParams, this.accessToken || undefined)
            ).subscribe({
              next: (response) => {
                if (response?.data?.rows?.[0]) {
                  this.currency = response.data.rows[0];
                  this.updateCurrencyProperties();
                  this.refreshBudgetPdfVm();
                }
              },
              error: (err) => {
                console.error('[BudgetPreview] Error en getCurrency:', {
                  currencyId: this.currencyId,
                  error: err,
                  status: err?.status,
                  message: err?.message
                });
              }
            });
          }

          // Calcular fecha actual formateada
          this.updateCurrentDateFormatted();

          // La fecha de emisión es el momento del renderizado, no la creación del presupuesto
          this.budgetDate = this.currentDateFormatted;
          if (this.saleOrder.expirationDate || this.saleOrder.validUntil) {
            this.expirationDate = this.formatDate(this.saleOrder.expirationDate || this.saleOrder.validUntil);
          }

          this.processLines(lines);
          this.refreshBudgetPdfVm();
        } else {
          if (!this.resolveIssueCurrency()) return;
          this.processLines(lines);
          this.refreshBudgetPdfVm();
        }
      },
      error: (err) => {
        if (!this.isCurrentRender(loadGeneration)) return;
        this.resetRenderReadiness();
        console.error('[BudgetPreview] Error en forkJoin (getSaleOrders/getSaleOrderLines/getOpportunityById):', {
          error: err,
          status: err?.status,
          statusText: err?.statusText,
          message: err?.message,
          url: err?.url,
          saleOrderId: saleOrderIdNum,
          leadId: this.leadId
        });
        this.errorMessage = 'Error al cargar los datos del presupuesto.';
        this.isLoading = false;
      }
    });
  }

  private extractOpportunityData(opportunityResponse: any): any | null {
    if (opportunityResponse?.data?.rows?.[0]) {
      return opportunityResponse.data.rows[0];
    }

    if (opportunityResponse?.data && !Array.isArray(opportunityResponse.data)) {
      return opportunityResponse.data;
    }

    return null;
  }

  hasValue(value: any): boolean {
    return value !== undefined && value !== null && value.toString().trim() !== '' && value.toString().trim() !== '-';
  }

  private cleanText(value: any): string {
    return this.hasValue(value) ? value.toString().trim() : '';
  }

  private joinName(entity: any): string {
    return [entity?.name, entity?.surName]
      .map(value => this.cleanText(value))
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  private firstText(...values: any[]): string {
    for (const value of values) {
      const cleanValue = this.cleanText(value);
      if (cleanValue) return cleanValue;
    }

    return '';
  }

  private getPartnerField(field: string, ...sources: any[]): string {
    return this.firstText(...sources.map(source => source?.[field]));
  }

  private samePartner(left: any, right: any): boolean {
    const leftId = Number(left?.id);
    const rightId = Number(right?.id);

    return Number.isFinite(leftId) && Number.isFinite(rightId) && leftId === rightId;
  }

  private pushItem(items: BudgetPdfItem[], value: any, icon?: string, label?: string): void {
    const cleanValue = this.cleanText(value);
    if (!cleanValue) return;
    items.push({ icon, label, value: cleanValue });
  }

  private pushFiscalIdentifier(items: BudgetPdfItem[], value: any): void {
    const cleanValue = this.cleanText(value);
    if (!cleanValue) return;

    const separatorIndex = cleanValue.indexOf(':');
    if (separatorIndex < 0) {
      items.push({ value: cleanValue });
      return;
    }

    const label = cleanValue.slice(0, separatorIndex).trim();
    const fiscalValue = cleanValue.slice(separatorIndex + 1).trim();
    if (!label || !fiscalValue) {
      items.push({ value: cleanValue });
      return;
    }

    items.push({ label, value: fiscalValue });
  }

  private buildTitle(): string {
    return resolveSaleOrderPdfTitle({
      name: this.saleOrder?.name,
      budgetCode:
        this.saleOrder?.budgetCode
        || this.saleOrder?.reference
        || this.saleOrder?.code
        || this.saleOrderReference,
    });
  }

  private refreshBudgetPdfVm(): void {
    const issuer = this.company || this.opportunity?.company || {};
    // CEC-638: manda la cotización y, si no trae el dato, se cae a la oportunidad. Antes las
    // dos mitades de la línea empezaban en la oportunidad y cada una por su cuenta, así que
    // el contacto editado en la cotización no llegaba al documento.
    const { company: customerCompany, contact, contactSources } = resolveBudgetCustomer({
      saleOrderCompany: this.saleOrder?.customerCompany,
      saleOrderContact: this.partner || this.saleOrder?.partner,
      opportunityCompany: this.opportunity?.customerCompany,
      opportunityContact: this.opportunity?.contact,
      opportunityPartner: this.opportunity?.partner
    });
    const commercialName = this.joinName(this.commercial) || this.joinName(this.opportunity?.user?.partner) || this.cleanText(this.opportunity?.user?.email);
    const customerCompanyName = this.cleanText(customerCompany?.name);
    const contactName = this.samePartner(contact, customerCompany) ? '' : this.joinName(contact);
    const customerAddressLines = buildBudgetCustomerAddressLines({
      contactSources,
      customerCompany
    });

    const issuerItems: BudgetPdfItem[] = [];
    this.pushItem(issuerItems, issuer.address, 'location_on');
    this.pushItem(issuerItems, issuer.contact, 'contacts_product');
    this.pushItem(issuerItems, issuer.email, 'mail');
    this.pushFiscalIdentifier(issuerItems, issuer.nif);

    const footerItems: BudgetPdfItem[] = [];
    this.pushItem(footerItems, issuer.address, 'location_on');
    this.pushItem(footerItems, issuer.contact, 'contacts_product');
    this.pushItem(footerItems, issuer.email, 'mail');
    this.pushFiscalIdentifier(footerItems, issuer.nif);

    this.budgetPdfVm = {
      issuer: {
        name: this.cleanText(issuer.name),
        country: this.cleanText(this.countryName),
        logo: issuer.logo,
        items: issuerItems,
        footerItems
      },
      customer: {
        nameLine: buildBudgetCustomerNameLine(
          customerCompanyName || this.cleanText(contact?.parent?.name || contact?.parentPartner?.name),
          contactName
        ),
        addressLines: customerAddressLines,
        commercial: commercialName,
        nif: this.firstText(
          this.getPartnerField('vat', ...contactSources),
          this.getPartnerField('vatNumber', ...contactSources),
          this.getPartnerField('nif', ...contactSources)
        )
      },
      title: this.buildTitle(),
      currencyLabel: this.cleanText(this.currencyCode),
      budgetDate: this.cleanText(this.budgetDate || this.currentDateFormatted),
      expirationDate: this.cleanText(this.expirationDate)
    };

    if (!this.hasValue(this.budgetPdfVm.issuer.logo)) {
      this.logoLoaded = true;
    }

    this.cdr.detectChanges();
    this.checkIfReady();
  }

  getVisibleIssuerItems(): BudgetPdfItem[] {
    return this.budgetPdfVm?.issuer.items || [];
  }

  getVisibleFooterItems(): BudgetPdfItem[] {
    return this.budgetPdfVm?.issuer.footerItems || [];
  }

  private trackVisibleFallback<T>(source: Observable<T>): Observable<T> {
    this.pendingVisibleFallbacks += 1;
    return source.pipe(finalize(() => {
      this.pendingVisibleFallbacks -= 1;
      this.startPaginationWhenVisibleDataIsStable();
    }));
  }

  private startPaginationWhenVisibleDataIsStable(): void {
    if (this.isLoading || this.pendingVisibleFallbacks !== 0) return;

    this.cdr.detectChanges();
    void this.paginateDocumentAfterLayout();
  }

  private loadCommercialFromUser(): void {
    if (!this.opportunity?.user) {
      return;
    }

    const user = this.opportunity.user;

    // Si el user tiene partner completo, usarlo directamente
    if (user.partner) {
      this.commercial = user.partner;
      this.refreshBudgetPdfVm();
      this.cdr.detectChanges();
      return;
    }

    // Si el user tiene partnerId pero no tiene el objeto partner, obtenerlo
    if (user.partnerId) {
      const partnerParams: GetParams = {
        startRow: 0,
        endRow: 1,
        sortDirection: 'asc',
        sortProperty: 'id',
        filterProperty: 'id',
        filterValue: user.partnerId.toString()
      };

      this.trackVisibleFallback(
        this.partnerService.getPartner(partnerParams, this.accessToken || undefined)
      ).subscribe({
        next: (response) => {
          if (response?.data?.rows?.[0]) {
            this.commercial = response.data.rows[0];
            this.refreshBudgetPdfVm();
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('[BudgetPreview] Error en getPartner (comercial):', {
            partnerId: user.partnerId,
            error: err,
            status: err?.status,
            message: err?.message
          });
        }
      });
    } else {
      // Si no hay partnerId, usar el user directamente como fallback
      this.commercial = {
        name: user.email?.split('@')[0] || '',
        surName: '',
        email: user.email || '',
        phone: null,
        nif: null
      };
      this.refreshBudgetPdfVm();
      this.cdr.detectChanges();
    }
  }

  private processLines(linesResponse: any): void {
    // Helper para extraer parsedValue
    const getParsedValue = (value: any): number => {
      if (typeof value === 'object' && value?.parsedValue !== undefined) {
        return value.parsedValue;
      }
      return value || 0;
    };

    // Helper para procesar taxes
    const processTaxes = (taxes: any[]): SaleOrderLineTaxRel[] => {
      if (!Array.isArray(taxes)) return [];
      return taxes.map((taxRel: any) => {
        const tax = taxRel.tax || {};
        return {
          id: taxRel.id,
          saleOrderLineId: taxRel.saleOrderLineId,
          taxId: taxRel.taxId || tax.id,
          tax: {
            id: tax.id,
            name: tax.name || '',
            description: tax.description,
            taxType: tax.taxType || '',
            fiscalValidity: tax.fiscalValidity || '',
            invoiceTag: tax.invoiceTag || '',
            amount: this.issueRates && (tax.amountType || '') === 'Fixed'
              ? convertFixedTax(getParsedValue(tax.amount), this.issueRates)
              : getParsedValue(tax.amount),
            amountType: tax.amountType || '',
            companyId: tax.companyId || 0,
            isActive: tax.isActive !== undefined ? tax.isActive : true
          }
        };
      });
    };

    // Mapeo único de línea: las dos formas de respuesta comparten mapper para que
    // un arreglo no pueda aplicarse solo a una de ellas.
    const mapLine = (line: any) => {
      const quantity = getParsedValue(line.quantity);
      const discount = getParsedValue(line.discount);
      const amounts: BudgetLineAmountsInput = {
        quantity,
        discount,
        priceUnit: getParsedValue(line.priceUnit),
        // El precio ya convertido a la divisa del presupuesto (CEC-632). Si la
        // linea va en esa misma divisa, coincide con `priceUnit`.
        convertedPriceUnit: getParsedValue(line.convertedPriceUnit),
        priceSubtotal: getParsedValue(line.priceSubtotal),
        priceTotal: getParsedValue(line.priceTotal),
      };

      const netUnitPrice = resolveNetUnitPrice(amounts);
      const netAmount = resolveNetLineAmount(amounts);
      const grossAmount = resolveGrossLineAmount(amounts);

      // CEC-570: convert once here; everything downstream reads the converted values.
      const rates = this.issueRates;
      const converted = rates
        ? convertLineAmounts({
            quantity,
            budgetUnitPrice: netUnitPrice,
            discount,
            budgetNet: netAmount,
            storedTax: resolveStoredLineTax({
              taxAmount: line.taxAmount ?? null,
              priceSubtotal: amounts.priceSubtotal,
              priceTotal: grossAmount,
            }),
          }, rates)
        : null;

      return {
        id: line.id,
        name: line.name || '',
        description: line.name || '',
        quantity: quantity,
        price: converted ? converted.unit : netUnitPrice,
        priceUnit: converted ? converted.unit : netUnitPrice,
        priceSubtotal: converted ? converted.net : amounts.priceSubtotal,
        // El impuesto guardado de la línea: el pie lo suma en vez de recalcularlo (CEC-669 / CEC-666).
        // Se pasa crudo para no confundir «no vino» con 0; lo normaliza el resumen fiscal.
        taxAmount: converted ? converted.tax : (line.taxAmount ?? null),
        netAmount: converted ? converted.net : netAmount,
        priceTotal: converted ? (converted.gross ?? converted.net) : grossAmount,
        discount: discount,
        amount: converted ? (converted.gross ?? converted.net) : grossAmount,
        isCost: line.isCost || false,
        currencyId: line.currencyId || this.currencyId,
        currency: line.currency || null, // Guardar currency completo si viene
        productId: line.productId,
        product: line.product || null, // Preservar product si existe
        supplierId: line.supplierId,
        unitOfMeasureId: line.unitOfMeasureId,
        unitOfMeasure: line.unitOfMeasure || null, // Guardar unitOfMeasure completo
        unitOfMeasureName: line.unitOfMeasure?.name,
        customerNote: line.customerNote || '',
        taxes: processTaxes(line.taxes || []),
        saleOrderId: line.saleOrderId || Number(this.saleOrderId)
      };
    };

    if (linesResponse?.data?.rows) {
      this.budgetLines = linesResponse.data.rows.map(mapLine);
    } else if (Array.isArray(linesResponse?.data)) {
      this.budgetLines = linesResponse.data.map(mapLine);
    } else {
      this.budgetLines = [];
    }
    this.budgetLines = canonicalizeBudgetLines(this.budgetLines);

    // Si no se obtuvo currency del sale order, obtenerlo de la primera lÃ­nea
    if (!this.currency && this.budgetLines.length > 0 && this.budgetLines[0].currency) {
      this.currency = this.budgetLines[0].currency;
    }

    // Si no se obtuvo currencyId del sale order, obtenerlo de las lÃ­neas
    if (!this.currencyId && this.budgetLines.length > 0) {
      this.currencyId = this.budgetLines[0].currencyId || null;
    }

    this.conditionsPaginationComplete = false;
    this.conditionsPages = [];
    this.calculateTotals();
    this.isLoading = false;
    this.startPaginationWhenVisibleDataIsStable();
  }

  private async paginateDocumentAfterLayout(): Promise<void> {
    const generation = this.resetRenderReadiness();
    try {
      await this.paginateLinesAfterLayout(generation);
      if (!this.isCurrentRender(generation)) return;
      await this.paginateConditionsAfterLayout(generation);
      if (!this.isCurrentRender(generation)) return;
      await this.publishRenderManifest(generation);
    } catch (error) {
      if (!this.isCurrentRender(generation)) return;
      this.resetRenderReadiness();
      this.pages = [];
      this.pageNumbers = [];
      this.totalPages = 0;
      this.conditionsPaginationComplete = false;
      this.isPdfReady = false;
      this.errorMessage = 'No se pudo calcular el layout completo del presupuesto.';
      console.error('[BudgetPreview] Error de paginación medida:', error);
    }
  }

  private isCurrentRender(generation: number): boolean {
    return generation === this.renderGeneration;
  }

  private async publishRenderManifest(generation: number): Promise<void> {
    this.cdr.detectChanges();
    const root = this.getMeasurementRoot();
    await waitForBudgetFinalLayout(document, root, {
      deadlineAt: this.getRenderDeadlineAt(),
      isDataStable: () => !this.isLoading
        && Boolean(this.saleOrder)
        && this.countryLoaded
        && this.pendingVisibleFallbacks === 0
        && this.conditionsPaginationComplete,
    });
    if (!this.isCurrentRender(generation)) return;

    const pages = Array.from(root.querySelectorAll<HTMLElement>(':scope > .page'));
    validateBudgetPageSafeAreas(root);
    const renderedFragments = Array.from(
      root.querySelectorAll<HTMLElement>('.services-table tbody tr[data-budget-line-id][data-fragment-key]')
    ).map(row => ({
      lineId: row.dataset['budgetLineId']!,
      fragmentKey: row.dataset['fragmentKey']!,
    }));
    const expectedLineIds = this.budgetLines.map((line, index) => `${line.id ?? index}`);
    const manifest = buildBudgetPdfRenderManifest({
      expectedLineIds,
      renderedFragments,
      expectedPageCount: this.pageNumbers.length,
      renderedPageCount: pages.length,
      summaryRequired: !this.unitary,
      summaryCount: root.querySelectorAll('.summary-section').length,
      conditionsExpected: this.conditionsPages.length > 0,
      conditionsCount: root.querySelectorAll('.conditions-section').length,
    });
    if (!this.isCurrentRender(generation)) return;
    this.renderManifest = manifest;
    this.isPdfReady = true;
    this.viewChecked = true;
    this.cdr.detectChanges();
  }

  private async paginateLinesAfterLayout(generation = this.renderGeneration): Promise<void> {
    if (!this.isCurrentRender(generation)) return;
    if (this.budgetLines.length === 0) {
      this.pages = paginateMeasuredBudgetLines([], {
        firstPageCapacity: 1,
        continuationPageCapacity: 1,
        summaryHeight: this.unitary ? 0 : 1,
        includeSummary: !this.unitary,
      });
      this.updateConditionsPageNumbers();
      return;
    }

    const probes = this.budgetLines.map((line, index) => this.createLineFragment(line, index, 1));
    this.pages = [
      { lines: probes, showSummary: false, continuation: false },
      { lines: [probes[0]], showSummary: !this.unitary, continuation: true },
    ];
    this.totalPages = 2;
    this.pageNumbers = [0, 1];
    this.cdr.detectChanges();
    const measurementRoot = this.getMeasurementRoot();
    await waitForBudgetMeasurementLayout(document, measurementRoot, {
      deadlineAt: this.getRenderDeadlineAt(),
      isDataStable: () => this.pendingVisibleFallbacks === 0,
    });
    if (!this.isCurrentRender(generation)) return;

    const renderedPages = document.querySelectorAll<HTMLElement>('.budget-preview-root .page');
    const firstPage = renderedPages?.[0];
    const continuationPage = renderedPages?.[1];
    if (!firstPage || !continuationPage) {
      this.pages = [];
      this.pageNumbers = [];
      this.totalPages = 0;
      this.isPdfReady = false;
      this.conditionsPaginationComplete = false;
      throw new Error('No se pudo medir la geometría del presupuesto.');
    }

    const firstCapacity = this.getMeasuredRowsCapacity(firstPage);
    const continuationCapacity = this.getMeasuredRowsCapacity(continuationPage);
    const summaryHeight = this.unitary
      ? 0
      : this.getOuterHeight(continuationPage.querySelector<HTMLElement>('.summary-section'));
    const rows = Array.from(firstPage.querySelectorAll<HTMLTableRowElement>('.services-table tbody tr'));
    const fragments = this.budgetLines.flatMap((line, index) => {
      const row = rows[index];
      if (!row) throw new Error(`No se pudo medir la línea ${line.id}.`);
      const description = this.getLineDescription(line);
      const note = this.getLineNote(line);
      return fragmentMeasuredBudgetLine(
        line,
        description,
        note,
        continuationCapacity,
        (_, fragmentDescription, fragmentNote, continuation) =>
          this.measureLineFragment(row, fragmentDescription, fragmentNote, continuation),
        `${line.id ?? index}`
      );
    });

    if (!this.isCurrentRender(generation)) return;
    this.pages = paginateMeasuredBudgetLines(fragments, {
      firstPageCapacity: firstCapacity,
      continuationPageCapacity: continuationCapacity,
      summaryHeight,
      includeSummary: !this.unitary,
    });
    this.updateConditionsPageNumbers();
    this.cdr.detectChanges();
  }

  getLinesForPage(pageIndex: number): BudgetLineFragment<BudgetLine>[] {
    return this.pages[pageIndex]?.lines || [];
  }

  getFragmentDescription(fragment: BudgetLineFragment<BudgetLine>): string {
    return fragment.description;
  }

  getFragmentNote(fragment: BudgetLineFragment<BudgetLine>): string {
    return fragment.note;
  }

  getLineDiscountPercent(line: BudgetLine): number {
    return resolveLineDiscountPercent(line);
  }

  getLineNote(line: BudgetLine): string {
    return (line.customerNote || '').toString().trim();
  }

  private getLineDescription(line: BudgetLine): string {
    return (line.product?.name || line.name || line.description || '').toString();
  }

  private createLineFragment(line: BudgetLine, index: number, height: number): BudgetLineFragment<BudgetLine> {
    return {
      key: `${line.id ?? index}`,
      value: line,
      description: this.getLineDescription(line),
      note: this.getLineNote(line),
      height,
      continuation: false,
    };
  }

  private getMeasuredRowsCapacity(page: HTMLElement): number {
    const body = page.querySelector<HTMLElement>('.services-table tbody');
    const footer = page.querySelector<HTMLElement>('.footer-section');
    if (!body || !footer) throw new Error('La tabla o el footer no están disponibles para medición.');
    return footer.getBoundingClientRect().top - body.getBoundingClientRect().top;
  }

  private getOuterHeight(element: HTMLElement | null): number {
    if (!element) throw new Error('El resumen no está disponible para medición.');
    const style = getComputedStyle(element);
    return element.getBoundingClientRect().height
      + parseFloat(style.marginTop || '0')
      + parseFloat(style.marginBottom || '0');
  }

  private measureLineFragment(
    sourceRow: HTMLTableRowElement,
    description: string,
    note: string,
    continuation: boolean
  ): number {
    const sourceTable = sourceRow.closest('table')!;
    const table = sourceTable.cloneNode(true) as HTMLTableElement;
    const body = table.querySelector('tbody')!;
    const row = sourceRow.cloneNode(true) as HTMLTableRowElement;
    body.replaceChildren(row);
    row.querySelector<HTMLElement>('.line-description')!.textContent = description;
    const noteElement = row.querySelector<HTMLElement>('.line-note');
    if (noteElement) noteElement.textContent = note;
    // La nota de descuento solo se pinta en el primer fragmento (CEC-586), asi que
    // se retira del clon al medir una continuacion: si no, se medirian fragmentos
    // mas altos de lo que luego se renderiza y sobrarian saltos de pagina.
    if (continuation) {
      row.querySelector<HTMLElement>('.line-discount')?.remove();
    }
    row.querySelectorAll<HTMLElement>('td:not(.col-description)').forEach(cell => {
      cell.style.visibility = continuation ? 'hidden' : 'visible';
    });
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;left:-10000px;top:0;visibility:hidden;width:${sourceTable.getBoundingClientRect().width}px`;
    host.appendChild(table);
    document.body.appendChild(host);
    const height = row.getBoundingClientRect().height;
    host.remove();
    return height;
  }
  private async paginateConditionsAfterLayout(generation = this.renderGeneration): Promise<void> {
    if (!this.isCurrentRender(generation)) return;

    try {
      this.cdr.detectChanges();
      await waitForBudgetMeasurementLayout(document, this.getMeasurementRoot(), {
        deadlineAt: this.getRenderDeadlineAt(),
        isDataStable: () => this.pendingVisibleFallbacks === 0,
      });
      if (!this.isCurrentRender(generation)) return;
      const conditionsPages = await paginateBudgetConditionsAsync(
        this.saleOrder?.terms,
        this.company?.terms,
        (html, pageKind) => this.measureConditionsFragmentForGeneration(html, pageKind, generation)
      );
      if (!this.isCurrentRender(generation)) return;
      this.conditionsPages = conditionsPages;
    } catch (error) {
      if (!this.isCurrentRender(generation)) return;
      if (error instanceof BudgetMeasurementTimeoutError) throw error;
      this.conditionsPages = paginateBudgetConditions(this.saleOrder?.terms, this.company?.terms);
    }

    this.updateConditionsPageNumbers();
    this.conditionsMeasurementHtml = '';
    this.cdr.detectChanges();
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    if (!this.isCurrentRender(generation)) return;
    this.conditionsPaginationComplete = true;
    this.checkIfReady();
  }

  private async measureConditionsFragmentForGeneration(
    html: string,
    _pageKind: ConditionsPageKind,
    generation: number
  ): Promise<boolean> {
    return retryBudgetConditionsMeasurement(async () => {
      if (!this.isCurrentRender(generation)) return false;
      const fits = await this.measureConditionsFragmentOnce(html);
      return this.isCurrentRender(generation) && fits;
    });
  }

  private async measureConditionsFragmentOnce(html: string): Promise<boolean> {
    const viewport = this.conditionsMeasurementViewport?.nativeElement;
    const content = this.conditionsMeasurementContent?.nativeElement;
    if (!viewport || !content) {
      throw new Error('Conditions measurement viewport is unavailable');
    }

    this.conditionsMeasurementHtml = html;
    this.cdr.detectChanges();
    await waitForBudgetMeasurementLayout(document, content, {
      deadlineAt: this.getRenderDeadlineAt(),
      isDataStable: () => this.pendingVisibleFallbacks === 0,
    });

    const capacity = this.getConditionsMeasurementCapacity(viewport);
    if (!Number.isFinite(capacity) || capacity <= 0) {
      throw new Error('Conditions measurement capacity is invalid');
    }

    const contentHeight = Math.max(content.scrollHeight, content.getBoundingClientRect().height);
    return Number.isFinite(contentHeight) && contentHeight <= capacity + 0.5;
  }

  private getMeasurementRoot(): HTMLElement {
    const root = document.querySelector<HTMLElement>('.budget-preview-root');
    if (!root) throw new Error('El presupuesto no está disponible para medición.');
    return root;
  }

  private getRenderDeadlineAt(): number {
    if (this.renderDeadlineAt <= Date.now()) {
      this.renderDeadlineAt = Date.now() + BUDGET_PDF_RENDER_DEADLINE_MS;
    }
    return this.renderDeadlineAt;
  }

  private getConditionsMeasurementCapacity(viewport: HTMLElement): number {
    const footer = viewport.parentElement?.querySelector<HTMLElement>('.footer-section');
    if (!footer) return Number.NaN;
    return footer.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
  }

  private updateConditionsPageNumbers(): void {
    if (this.conditionsPages.length > 0) {
      this.conditionsStartPage = this.pages.length;
      this.totalPages = Math.max(1, this.conditionsStartPage + this.conditionsPages.length);
    } else {
      this.conditionsStartPage = this.pages.length;
      this.totalPages = Math.max(1, this.pages.length);
    }

    this.pageNumbers = Array.from({ length: this.totalPages }, (_, index) => index);
  }

  /**
   * Obtiene las condiciones para una pÃ¡gina especÃ­fica
   */
  getConditionsForPage(pageIndex: number): string | null {
    const conditionsPageIndex = pageIndex - this.conditionsStartPage;
    if (conditionsPageIndex >= 0 && conditionsPageIndex < this.conditionsPages.length) {
      return this.conditionsPages[conditionsPageIndex];
    }
    return null;
  }

  /**
   * Verifica si una pÃ¡gina es una pÃ¡gina de condiciones
   */
  isConditionsPage(pageIndex: number): boolean {
    return pageIndex >= this.conditionsStartPage && pageIndex < this.conditionsStartPage + this.conditionsPages.length;
  }

  /**
   * Verifica si una pagina tiene el resumen.
   * El resumen debe aparecer antes de las condiciones generales.
   */
  hasSummaryOnPage(pageIndex: number): boolean {
    return this.pages[pageIndex]?.showSummary === true;
  }

  /**
   * Verifica si una pÃ¡gina de condiciones tiene contenido real (texto visible)
   * Se usa para decidir si mostrar el tÃ­tulo de "Condiciones Generales"
   */
  hasConditionsContent(pageIndex: number): boolean {
    if (!this.isConditionsPage(pageIndex)) return false;

    const content = this.getConditionsForPage(pageIndex);
    if (!content) return false;

    // Eliminar tags HTML y espacios para verificar si hay texto real
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    // TambiÃ©n considerar imÃ¡genes o lÃ­neas horizontales como contenido
    const hasMedia = content.includes('<img') || content.includes('<hr');

    return textContent.length > 0 || hasMedia;
  }

  /** ISO code echoed on the readiness marker: the issue currency when converting, else the budget one. */
  get issueCurrencyEcho(): string {
    return this.issueCurrency.kind === 'convert' ? this.issueCurrency.issueCurrency : this.currencyCode;
  }

  /** Rates echoed on the readiness marker, verbatim as received, or `none` without conversion. */
  get issueRatesEcho(): string {
    return this.issueCurrency.kind === 'convert' ? this.issueCurrency.echoRates : 'none';
  }

  private get issueRates(): IssueCurrencyRates | null {
    return this.issueCurrency.kind === 'convert'
      ? { from: this.issueCurrency.rateFrom, to: this.issueCurrency.rateTo }
      : null;
  }

  /** Reads the render parameters; on an invalid combination it stops the render (no readiness). */
  private resolveIssueCurrency(): boolean {
    const query = (this.route.snapshot?.queryParams ?? {}) as Record<string, unknown>;
    this.issueCurrency = parseIssueCurrencyParams(
      { get: (name: string) => (typeof query[name] === 'string' ? (query[name] as string) : null) },
      this.currencyId,
    );
    if (this.issueCurrency.kind === 'error') {
      this.errorMessage = 'Parametros de moneda de emision no validos.';
      this.isLoading = false;
      return false;
    }
    return true;
  }

  private calculateTotals(): void {
    // Los tres importes salen de las MISMAS funciones que alimentan la columna
    // «Importe neto», para que el documento no pueda contradecirse consigo mismo.
    // `priceSubtotal` llega del backend ya descontado: descontarlo aquí otra vez
    // era lo que hacía que el Subtotal no cuadrase con la suma de la columna
    // (CEC-586, verificado en el documento S814476 de DEV).
    this.baseAmount = this.budgetLines.reduce(
      (sum, line) => sum + resolveNetLineAmountBeforeDiscount(line), 0);

    this.totalAmount = this.budgetLines.reduce(
      (sum, line) => sum + resolveNetLineAmount(line), 0);

    this.totalDiscount = this.baseAmount - this.totalAmount;
    this.showDiscountBreakdown = shouldDiscloseBudgetDiscount(this.baseAmount, this.totalAmount);

    const taxSummary = buildBudgetTaxSummary(this.budgetLines);
    this.totalIva = taxSummary.totalTaxAmount;

    this.totalWithIva = this.totalAmount + this.totalIva;

    if (this.issueRates) {
      // Converted amounts carry 4 decimals: sum them exactly so the printed total always equals
      // the printed net plus the printed taxes (the budget-currency path keeps the float sums above).
      this.baseAmount = sumDecimal(this.budgetLines.map(line => resolveNetLineAmountBeforeDiscount(line)));
      this.totalAmount = sumDecimal(this.budgetLines.map(line => resolveNetLineAmount(line)));
      this.totalDiscount = sumDecimal([this.baseAmount, -this.totalAmount]);
      this.totalIva = sumDecimal(this.budgetLines.map(line => resolveStoredLineTax(line) ?? 0));
      this.totalWithIva = sumDecimal([this.totalAmount, this.totalIva]);
    }
  }

  formatDate(date: string | Date): string {
    return formatBudgetDateOnly(date);
  }

  goBack(): void {
    this.router.navigate(['/opportunities']);
  }

  private updateCurrentDateFormatted(): void {
    this.currentDateFormatted = formatBudgetDateOnly(new Date());
  }


  private updateCurrencyProperties(): void {
    const isoCode = this.issueCurrency.kind === 'convert'
      ? this.issueCurrency.issueCurrency
      : this.cleanText(this.currency?.isoCode).toUpperCase();
    this.currencyCode = /^[A-Z]{3}$/.test(isoCode) ? isoCode : '';

    if (this.currency?.name) {
      this.currencyName = this.currency.name;
    } else {
      this.currencyName = '';
    }
  }

  private loadCountryFromCompany(): void {
    if (!this.company?.countryId) {
      this.countryName = '';
      this.countryLoaded = true;
      this.checkIfReady();
      return;
    }

    const countryParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: this.company.countryId.toString()
    };

    this.trackVisibleFallback(
      this.locationTransportService.getCountry(countryParams, this.accessToken || undefined)
    ).subscribe({
      next: (response) => {
        if (response?.data?.rows?.[0]) {
          const country = response.data.rows[0];
          this.countryName = country.name || '';
        } else {
          this.countryName = '';
        }
        this.countryLoaded = true;
        this.refreshBudgetPdfVm();
        this.cdr.detectChanges();
        this.checkIfReady();
      },
      error: (err) => {
        console.error('[BudgetPreview] Error en getCountry:', {
          countryId: this.company.countryId,
          error: err,
          status: err?.status,
          message: err?.message
        });
        this.countryName = '';
        this.countryLoaded = true;
        this.refreshBudgetPdfVm();
        this.checkIfReady();
      }
    });
  }

  // MÃ©todo helper para mostrar los impuestos desde el array taxes
  getTaxPercentage(line: BudgetLine): string {
    if (!line.taxes || line.taxes.length === 0) {
      return '0%';
    }

    const taxStrings: string[] = [];

    line.taxes.forEach((taxRel) => {
      const tax = taxRel.tax;
      if (!tax) return;

      const taxAmount = tax.amount || 0;
      const amountType = tax.amountType || 'Percent';

      if (amountType === 'Fixed') {
        // Para taxes fijos, mostrar el monto con la moneda
        taxStrings.push(`${taxAmount.toFixed(2)} ${this.currencyCode}`);
      } else {
        // Para taxes porcentuales, mostrar el porcentaje
        taxStrings.push(`${taxAmount}%`);
      }
    });

    // Si hay mÃºltiples taxes, unirlos con " + "
    return taxStrings.length > 0 ? taxStrings.join(' + ') : '0%';
  }
}
